import { useEffect, useState, createContext, PropsWithChildren } from "react";
import { supabase } from "@/lib/supabase-client";
import useAuth from "@/hooks/useAuth";
import { timecardService } from "@/services/timecardService";
import type { RealtimeContextType } from "@/types/providers/realtime";

export const RealtimeContext = createContext<RealtimeContextType | undefined>(undefined);

export function RealtimeProvider({ children }: PropsWithChildren) {
  const { user, tenantId } = useAuth();
  const [timeCardsIssueCount, setTimeCardsIssueCount] = useState(0);

  // initial time_cards issue count
  useEffect(() => {
    if (!user) return;
    const getUnapprovedTimecardsCount = async () => {
      try {
        const count = await timecardService.getUnapprovedTimecardsCount();
        setTimeCardsIssueCount(count);
      } catch {
        setTimeCardsIssueCount(0);
      }
    };
    getUnapprovedTimecardsCount();
  }, [user]);

  // realtime: refresh time_cards issue count on changes
  useEffect(() => {
    if (!user?.id || !tenantId) return;

    const ch = supabase
      .channel("schema-db-changes")
      .on("postgres_changes", { event: "*", schema: "public", table: "time_cards" }, async () => {
        try {
          const count = await timecardService.getUnapprovedTimecardsCount();
          setTimeCardsIssueCount(count);
        } catch {
          setTimeCardsIssueCount(0);
        }
      })
      .subscribe();

    return () => {
      ch.unsubscribe();
    };
  }, [user?.id, tenantId]);

  return <RealtimeContext.Provider value={{ timeCardsIssueCount }}>{children}</RealtimeContext.Provider>;
}
